import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import { advantages } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const response = await fetch('http://w0ap.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(errMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩国漫画网免费韩漫在线阅读平台"
        description="韩国漫画网成立于2023年，致力于为广大韩漫爱好者提供海量免费、高清画质的韩国漫画在线阅读服务。了解我们的品牌故事、平台优势与联系方式。"
        keywords="关于韩国漫画网,韩漫平台介绍,免费漫画网站,韩国漫画关于我们,联系韩漫网"
        canonicalUrl="http://w0ap.com/about"
        breadcrumbs={[
          { label: '首页', path: '/' },
          { label: '关于我们' },
        ]}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于韩国漫画网',
          description: '韩国漫画网 - 免费韩漫在线阅读平台，成立于2023年，为全球韩漫爱好者提供优质阅读服务。',
          url: 'http://w0ap.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '韩国漫画网',
            url: 'http://w0ap.com/',
          },
        }}
      />

      <Navbar />

      <main>
        {/* 品牌Hero */}
        <section className="relative h-[45vh] min-h-[350px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 comic-dot-pattern"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-comic-dark via-comic-dark/95 to-comic-dark"></div>
          <div className="relative z-10 text-center px-4">
            <p className="text-comic-accent text-xs font-medium tracking-widest mb-4">ABOUT US</p>
            <h1 className="text-4xl md:text-7xl font-black text-white">
              关于<span className="text-comic-primary">韩漫网</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base mt-4 max-w-xl mx-auto">
              深耕韩漫领域，用心打造极致阅读体验
            </p>
          </div>
        </section>

        {/* 面包屑 */}
        <BreadcrumbNav
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* 品牌介绍 - 左右不对称 */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* 左侧：品牌故事 */}
              <div className="lg:col-span-5">
                <span className="inline-block px-3 py-1 rounded-full text-xs border border-comic-border text-gray-400 mb-4">
                  品牌故事
                </span>
                <h2 className="text-2xl md:text-3xl font-black text-white mb-1">
                  用心打造<span className="text-comic-primary">极致</span>体验
                </h2>
                <p className="text-gray-500 text-sm mb-6">始于2023，热爱不止</p>
                <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-4 mb-8">
                  <p>
                    韩国漫画网成立于2023年，由一群热爱韩国漫画的资深漫迷共同创办。创始团队深知漫画爱好者对阅读体验的高要求——画质要清晰、更新要及时、分类要精准、操作要便捷。正是源于这份热爱和理解，我们打造了这个专注于韩漫的在线阅读平台。
                  </p>
                  <p>
                    从最初收录数百部作品到如今拥有<strong className="text-comic-primary">超过5000部</strong>精心整理的韩漫资源，韩国漫画网始终秉持"让好漫画触手可及"的理念。我们与多家韩国漫画平台建立了合作关系，确保第一时间为读者带来最新的连载内容。
                  </p>
                  <p>
                    韩国漫画网的用户遍布全国乃至全球华人社区。无论是校园里偷偷看漫画的学生，还是下班后放松身心的上班族，在这里都能找到属于自己的阅读乐趣。<strong className="text-white">无需注册、无需付费、无广告干扰</strong>——我们只想让阅读回归纯粹的快乐。
                  </p>
                </div>
                <div className="flex items-center gap-2 text-comic-primary text-sm">
                  <i className="ri-heart-line"></i>
                  <span>已有超过百万漫迷选择我们</span>
                </div>
              </div>

              {/* 右侧：两张插画卡片 */}
              <div className="lg:col-span-7">
                <div className="relative">
                  <div className="w-48 h-56 md:w-60 md:h-72 rounded-2xl overflow-hidden border border-comic-border relative z-20 bg-comic-card">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20creative%20team%20working%20on%20comics%2C%20warm%20coral%20and%20rose%20pink%20tones%2C%20cozy%20studio%20atmosphere%2C%20professional%20illustration%20quality%2C%20clean%20dark%20background%2C%20editorial%20style&width=500&height=600&seq=about-team-1&orientation=portrait"
                      alt="韩国漫画网编辑团队"
                      title="韩国漫画网 - 编辑团队"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute top-12 left-20 md:left-24 w-64 h-80 md:w-72 md:h-96 rounded-2xl overflow-hidden border border-comic-border z-10 bg-comic-card">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20readers%20enjoying%20comics%20on%20devices%2C%20warm%20coral%20and%20rose%20pink%20tones%2C%20comfortable%20reading%20atmosphere%2C%20professional%20illustration%20quality%2C%20clean%20dark%20background&width=600&height=800&seq=about-team-2&orientation=portrait"
                      alt="韩国漫画网阅读场景"
                      title="韩国漫画网 - 用户阅读场景"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 平台优势 */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 comic-dot-pattern">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-12">
              <p className="text-comic-accent text-xs font-medium tracking-widest mb-3">WHY CHOOSE US</p>
              <h2 className="text-3xl md:text-5xl font-black text-white">
                选择<span className="text-comic-primary">韩漫网</span>的理由
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {advantages.map((item) => (
                <article
                  key={item.title}
                  className="bg-comic-card rounded-2xl p-6 md:p-8 transition-all duration-300 hover:bg-comic-card-hover"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-comic-accent/10 text-comic-accent mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 联系合作 */}
        <section id="contact" className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden">
              {/* 左侧 */}
              <div className="relative bg-[#1C1C1E] min-h-[350px] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20creative%20collaboration%20scene%2C%20warm%20coral%20and%20beige%20tones%2C%20friendly%20professional%20atmosphere%2C%20editorial%20style%2C%20clean%20composition&width=900&height=700&seq=about-contact-scene&orientation=landscape"
                  alt="联系我们"
                  title="韩国漫画网 - 合作联系"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/30"></div>
                <div className="absolute left-6 bottom-6">
                  <p className="text-white/60 text-xs mb-3 tracking-widest">CONTACT</p>
                  <p className="text-white font-black text-3xl md:text-5xl leading-tight">
                    期待与<span className="text-comic-primary">你</span>
                    <br />
                    取得联系
                  </p>
                </div>
              </div>

              {/* 右侧 */}
              <div className="bg-white p-8 md:p-12 flex flex-col justify-center">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">联系合作</h2>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-comic-primary/10 text-comic-primary flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">合作邮箱</p>
                      <p className="text-gray-900 text-sm font-medium">hello@w0ap.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-comic-primary/10 text-comic-primary flex-shrink-0">
                      <i className="ri-chat-smile-2-line"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">商务合作</p>
                      <p className="text-gray-900 text-sm font-medium">hello@w0ap.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-comic-primary/10 text-comic-primary flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">办公地址</p>
                      <p className="text-gray-900 text-sm font-medium">北京市朝阳区漫画创意产业园</p>
                    </div>
                  </div>
                </div>

                {/* 快速留言表单 */}
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <input type="text" name="name" placeholder="您的称呼" required className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-comic-primary transition-colors" />
                  <input type="email" name="email" placeholder="您的邮箱" required className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-comic-primary transition-colors" />
                  <textarea name="message" placeholder="留言内容（500字以内）" maxLength={500} rows={3} required className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-comic-primary transition-colors resize-none"></textarea>

                  {/* 蜜罐字段 */}
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form-website-alt" />

                  <button type="submit" disabled={formStatus === 'loading'} className="w-full px-6 py-3 rounded-full bg-comic-primary text-white font-bold text-sm hover:bg-comic-primary-hover transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {formStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </>
                    ) : (
                      <>
                        发送留言
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-green-600 text-sm text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-red-500 text-sm text-center">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '韩国漫画网需要注册才能看漫画吗？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '不需要。韩国漫画网无需注册即可畅享所有免费漫画内容，打开网站即可开始阅读。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '韩国漫画网的漫画是免费的吗？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '韩国漫画网提供大量免费韩漫资源供读者在线阅读。我们每天更新最新热门作品，覆盖校园、恋爱、奇幻、热血等全品类题材。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '韩国漫画网支持哪些设备阅读？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '韩国漫画网完美适配电脑、平板和手机等多终端设备，采用响应式设计，自动适配不同屏幕尺寸，随时随地畅享流畅的漫画阅读体验。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '如何与韩国漫画网进行商务合作？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '欢迎通过合作邮箱 hello@w0ap.com 或 hello@w0ap.com 与我们联系，期待与内容创作者、版权方及各类合作伙伴开展合作。',
                  },
                },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}